import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getMustChangePassword, getCurrentUserProfile } from '../services/api';

/**
 * AdminProtectedRoute component that ensures:
 * 1. User is authenticated
 * 2. User has changed their password (if required)
 * 3. User is an ADMIN (not MANAGER or EMPLOYE)
 */
const AdminProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuthorization = async () => {
      // Check authentication
      if (!isAuthenticated()) {
        setIsAuthorized(false);
        return;
      }

      // Check if user must change password
      if (getMustChangePassword()) {
        setIsAuthorized(false);
        return;
      }

      // Check user role
      try {
        const userData = await getCurrentUserProfile();
        const role = userData?.role;
        setUserRole(role);
        
        // Only ADMIN can access admin routes
        if (role === 'ADMIN') {
          setIsAuthorized(true);
        } else {
          // Manager or Employee trying to access admin route - not authorized
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setIsAuthorized(false);
      }
    };

    checkAuthorization();
  }, []);

  // Check authentication
  if (!isAuthenticated()) {
    // Redirect to login, preserving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user must change password
  if (getMustChangePassword()) {
    // Redirect to reset-password, preserving the attempted location
    return <Navigate to="/reset-password" state={{ from: location }} replace />;
  }

  // Check authorization - if not authorized, redirect based on role
  if (isAuthorized === false) {
    if (userRole === 'MANAGER') {
      return <Navigate to="/manager-dashboard" replace />;
    } else if (userRole === 'EMPLOYE') {
      return <Navigate to="/employee-dashboard" replace />;
    } else {
      // Fallback to login if role is unknown
      return <Navigate to="/login" replace />;
    }
  }

  // User is authenticated, has changed password, and is authorized (ADMIN) - allow access
  // Render children immediately while role check happens in background
  return children;
};

export default AdminProtectedRoute;

